
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { List, Tag, Crown, Menu, MapPin, Palette, ChevronDown, Heart, Building2, Users, Snowflake } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState } from "react";

interface HeaderProps {
  onShowAllVacations: () => void;
  onShowDeals: () => void;
  onShowLuxury: () => void;
  onShowRondreizen: () => void;
  onShowThema: () => void;
  onShowFavoritesComparison: () => void;
  onGoHome: () => void;
  currentView: string;
}

export const Header = ({ onShowAllVacations, onShowDeals, onShowLuxury, onShowRondreizen, onShowThema, onShowFavoritesComparison, onGoHome, currentView }: HeaderProps) => {
  const isMobile = useIsMobile();
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const handleMenuAction = (action: () => void) => {
    action();
    setIsSheetOpen(false);
  };

  const handleJongerenreizen = () => {
    window.location.href = '/jongerenreizen';
  };

  const handleWintersport = () => {
    window.location.href = '/wintersport';
  };

  const MobileMenu = () => (
    <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon" className="border-orange-300 hover:bg-orange-50">
          <Menu className="h-4 w-4" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-80">
        <div className="flex flex-col space-y-4 mt-8">
          <h2 className="text-lg font-semibold text-orange-700 mb-4">Menu</h2>
          
          <Button 
            variant="outline" 
            onClick={() => handleMenuAction(onShowDeals)} 
            className="flex items-center gap-2 justify-start border-orange-300 hover:bg-orange-50"
          >
            <Tag className="h-4 w-4" />
            Deals
          </Button>
          
          <div className="space-y-2">
            <Button 
              variant="outline" 
              onClick={() => handleMenuAction(onShowThema)} 
              className="flex items-center gap-2 justify-start border-orange-300 hover:bg-orange-50 w-full"
            >
              <Palette className="h-4 w-4" />
              Thema's
            </Button>
            
            <div className="ml-4 space-y-2">
              <Button 
                variant="ghost" 
                onClick={() => handleMenuAction(onShowLuxury)} 
                className="flex items-center gap-2 justify-start text-sm w-full"
              >
                <Crown className="h-3 w-3" />
                Luxe
              </Button>
              
              <Button 
                variant="ghost" 
                onClick={() => handleMenuAction(onShowRondreizen)} 
                className="flex items-center gap-2 justify-start text-sm w-full"
              >
                <MapPin className="h-3 w-3" />
                Rondreizen
              </Button>
              
              <Button 
                variant="ghost" 
                onClick={() => handleMenuAction(handleWintersport)} 
                className="flex items-center gap-2 justify-start text-sm w-full"
              >
                <Snowflake className="h-3 w-3" />
                Wintersport
              </Button>
              
              <Button 
                variant="ghost" 
                onClick={() => handleMenuAction(handleJongerenreizen)} 
                className="flex items-center gap-2 justify-start text-sm w-full"
              >
                <Users className="h-3 w-3" />
                Jongerenreizen
              </Button>
              
              <Button 
                variant="ghost" 
                onClick={() => handleMenuAction(() => window.location.href = '/thema/reisaanbieder')} 
                className="flex items-center gap-2 justify-start text-sm w-full"
              >
                <Building2 className="h-3 w-3" />
                Reisaanbieders
              </Button>
              
              <div className="border-t pt-2 mt-2">
                <Button 
                  variant="ghost" 
                  onClick={() => handleMenuAction(onShowThema)} 
                  className="flex items-center gap-2 justify-start text-sm w-full font-medium"
                >
                  <Palette className="h-3 w-3" />
                  Bekijk alle thema's
                </Button>
              </div>
            </div>
          </div>
          
          <Button 
            variant="outline" 
            onClick={() => handleMenuAction(onShowFavoritesComparison)} 
            className="flex items-center gap-2 justify-start border-orange-300 hover:bg-orange-50"
          >
            <Heart className="h-4 w-4" />
            Favorieten vergelijken
          </Button>
          
          <Button 
            variant="outline" 
            onClick={() => handleMenuAction(onShowAllVacations)} 
            className="flex items-center gap-2 justify-start border-orange-300 hover:bg-orange-50"
          >
            <List className="h-4 w-4" />
            Alle vakanties
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );

  const DesktopNav = () => (
    <nav className="flex items-center gap-3">
      <Button variant="outline" onClick={onShowDeals} className="flex items-center gap-2 border-orange-300 hover:bg-orange-50">
        <Tag className="h-4 w-4" />
        Deals
      </Button>
      
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="flex items-center gap-2 border-orange-300 hover:bg-orange-50">
            <Palette className="h-4 w-4" />
            Thema's
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          <DropdownMenuItem onClick={onShowLuxury} className="flex items-center gap-2">
            <Crown className="h-4 w-4" />
            Luxe
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onShowRondreizen} className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Rondreizen
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleWintersport} className="flex items-center gap-2">
            <Snowflake className="h-4 w-4" />
            Wintersport
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleJongerenreizen} className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Jongerenreizen
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => window.location.href = '/thema/reisaanbieder'} className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Reisaanbieders
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onShowThema} className="flex items-center gap-2 font-medium">
            <Palette className="h-4 w-4" />
            Bekijk alle thema's
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      
      <Button variant="outline" onClick={onShowFavoritesComparison} className="flex items-center gap-2 border-orange-300 hover:bg-orange-50">
        <Heart className="h-4 w-4" />
      </Button>
      
      <Button variant="outline" onClick={onShowAllVacations} className="flex items-center gap-2 border-orange-300 hover:bg-orange-50">
        <List className="h-4 w-4" />
        Alle vakanties
      </Button>
    </nav>
  );

  return (
    <header className="bg-gradient-to-r from-amber-50 to-orange-100 shadow-sm border-b border-orange-200 sticky top-0 z-50">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 
              className="text-2xl font-bold text-orange-600 cursor-pointer hover:text-orange-700 transition-colors"
              onClick={onGoHome}
            >
              Vakantie Uitzoeken
            </h1>
          </div>
          
          {isMobile ? <MobileMenu /> : <DesktopNav />}
        </div>
      </div>
    </header>
  );
};
